// mount.jsx — extraído de index.html el 2026-08-28 (Etapa 1, Bloque C).
//
// Punto de entrada del home: define App() y monta React sobre #root. Se carga
// EL ÚLTIMO de las seis entradas de public/home/, porque consume lo que las otras
// cinco declaran como globales (useTweaks, TweaksPanel, Hero, RegionsSection...).
//
// Copia exacta del bloque original salvo un cambio deliberado, documentado abajo:
// TWEAK_DEFAULTS se lee de window.CRMAPS_TWEAK_DEFAULTS en vez de estar incrustado.

    (() => {

    const APP_I18N = window.CRMAPS_I18N || {};
    const getInitialLang = () => {
      // SEO wrappers /en/ and /es/ inject window.CRMAPS_ROUTE — the URL language wins.
      const routeLang = window.CRMAPS_ROUTE && window.CRMAPS_ROUTE.lang;
      if (routeLang === "es" || routeLang === "en") return routeLang;
      try {
        const saved = window.localStorage.getItem("crmaps-lang");
        if (saved === "es" || saved === "en") return saved;
      } catch (err) {}
      return (navigator.language || "").toLowerCase().startsWith("es") ? "es" : "en";
    };

    // Los valores por defecto del panel de ajustes NO viven aquí: siguen en
    // index.html, dentro de los marcadores /*EDITMODE-BEGIN*/ y /*EDITMODE-END*/.
    // La herramienta de diseño externa (el host que escucha __edit_mode_set_keys)
    // reescribe ese bloque EN EL DISCO buscando esos marcadores en el HTML.
    // Traerlo a este archivo compilado rompería ese flujo en silencio, así que se
    // lee como global. Sin el bloque en el HTML la app no arranca, a propósito:
    // un fallo visible es preferible a un home que se dibuja con ajustes vacíos.
    const TWEAK_DEFAULTS = window.CRMAPS_TWEAK_DEFAULTS;

    function App() {
      const [tweaks, setTweak] = window.useTweaks(TWEAK_DEFAULTS);
      const [activeSection, setActiveSection] = React.useState("");
      const [lang, setLang] = React.useState(getInitialLang);
      const lastChapter2 = React.useRef(0);
      const copy = APP_I18N[lang] || APP_I18N.en;

      const changeLanguage = (nextLang) => {
        try {
          window.localStorage.setItem("crmaps-lang", nextLang);
        } catch (err) {}
        // On the SEO wrappers, navigate to the other language's URL (preserve hash).
        const route = window.CRMAPS_ROUTE;
        const target = route && route[nextLang];
        if (target && target !== window.location.pathname) {
          window.location.href = target + window.location.hash;
          return;
        }
        setLang(nextLang);
      };

      // active-section observer
      React.useEffect(() => {
        const ids = ["what","regions","directory","categories","mobile","community","navigate","contact"];
        const els = ids.map(id => document.getElementById(id)).filter(Boolean);
        const io = new IntersectionObserver((entries) => {
          entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
        }, { rootMargin: "-30% 0px -50% 0px" });
        els.forEach(el => io.observe(el));
        return () => io.disconnect();
      }, []);

      // Scroll to hash on initial load (handles navigation from other pages, e.g. Tide_Chart.html → index.html#community)
      React.useEffect(() => {
        const hash = window.location.hash.slice(1);
        if (!hash) return;
        const tryScroll = () => {
          const el = document.getElementById(hash);
          if (el) {
            el.scrollIntoView({ behavior: "instant", block: "start" });
          } else {
            requestAnimationFrame(tryScroll);
          }
        };
        requestAnimationFrame(tryScroll);
      }, []);

      React.useEffect(() => {
        const target = document.getElementById("regions");
        if (!target) return undefined;
        const io = new IntersectionObserver(([entry]) => {
          if (!entry?.isIntersecting) return;
          const now = Date.now();
          if (now - lastChapter2.current < 900) return;
          lastChapter2.current = now;
          window.dispatchEvent(new CustomEvent("crmaps:chapter2-enter"));
        }, { threshold: 0.35 });
        io.observe(target);
        return () => io.disconnect();
      }, []);

      // body data attrs from tweaks
      React.useEffect(() => {
        document.body.dataset.density = tweaks.density;
        document.body.dataset.radius = tweaks.radius;
        document.body.dataset.typeweight = tweaks.typeWeight;
      }, [tweaks.density, tweaks.radius, tweaks.typeWeight]);

      React.useEffect(() => {
        document.documentElement.lang = lang;
        document.title = copy.meta.title;
      }, [lang, copy.meta.title]);

      React.useEffect(() => {
        const pages = document.querySelectorAll(".hero, .section, .site-footer");
        pages.forEach(page => {
          if (page.querySelector(":scope > .snap-end-marker")) return;
          const marker = document.createElement("span");
          marker.className = "snap-end-marker";
          marker.setAttribute("aria-hidden", "true");
          page.appendChild(marker);
        });
        return () => {
          document.querySelectorAll(".snap-end-marker").forEach(marker => marker.remove());
        };
      }, [tweaks]);

      // reveal-on-scroll
      React.useEffect(() => {
        const io = new IntersectionObserver((entries) => {
          entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("is-in"); });
        }, { threshold: 0.15 });
        document.querySelectorAll(".reveal, .reveal-stagger").forEach(el => io.observe(el));
        return () => io.disconnect();
      }, [tweaks]);

      // hero palette overrides
      const heroPalettes = {
        blue:    "radial-gradient(ellipse 90% 60% at 70% 20%, rgba(35,170,224,0.4) 0%, transparent 60%), radial-gradient(ellipse 80% 60% at 20% 90%, rgba(0,136,62,0.28) 0%, transparent 60%), linear-gradient(165deg, #023a6a 0%, #0061b4 35%, #084b88 100%)",
        sunset:  "radial-gradient(ellipse 90% 60% at 70% 20%, rgba(249,115,22,0.4) 0%, transparent 60%), radial-gradient(ellipse 80% 60% at 20% 90%, rgba(242,201,76,0.3) 0%, transparent 60%), linear-gradient(165deg, #6b1f1d 0%, #c0561e 50%, #2a1414 100%)",
        jungle:  "radial-gradient(ellipse 90% 60% at 70% 20%, rgba(112,181,44,0.4) 0%, transparent 60%), radial-gradient(ellipse 80% 60% at 20% 90%, rgba(33,165,167,0.4) 0%, transparent 60%), linear-gradient(165deg, #024f25 0%, #00883e 45%, #036b32 100%)",
        ink:     "radial-gradient(ellipse 90% 60% at 70% 20%, rgba(35,170,224,0.25) 0%, transparent 60%), linear-gradient(165deg, #050a14 0%, #0a1f33 60%, #021526 100%)",
      };

      return (
	    <React.Fragment>
	          <window.FloatNav activeSection={activeSection} lang={lang} onLanguageChange={changeLanguage} copy={copy} />
	          <style>{`.hero{ background: ${heroPalettes[tweaks.heroPalette]} !important; }`}</style>
	          <window.Hero copy={copy} lang={lang} />
          {tweaks.showWhat && <window.WhatSection copy={copy} />}
          {tweaks.showRegions && <window.RegionsSection copy={copy} />}
          {tweaks.showCategories && <window.DirectorySection copy={copy} lang={lang} />}
          {tweaks.showMobile && <window.MobileSection copy={copy} />}
          {tweaks.showCommunity && <window.SocialSection copy={copy} />}
          {tweaks.showNavigate && <window.NavSection copy={copy} />}
          {false && tweaks.showAnalytics && <window.AnalyticsSection />}
          {tweaks.showContact && <window.ContactSection copy={copy} />}
          <window.Footer copy={copy} lang={lang} />

          <window.TweaksPanel title="Tweaks">
            <window.TweakSection title="Hero">
              <window.TweakSelect label="Hero palette" value={tweaks.heroPalette} onChange={v => setTweak("heroPalette", v)}
                options={[
                  {value:"blue",   label:"Pacific Blue"},
                  {value:"jungle", label:"Tropical Jungle"},
                  {value:"sunset", label:"Tamarindo Sunset"},
                  {value:"ink",    label:"Midnight Ink"},
                ]} />
            </window.TweakSection>
            <window.TweakSection title="Layout">
              <window.TweakRadio label="Density" value={tweaks.density} onChange={v => setTweak("density", v)}
                options={[{value:"compact",label:"Tight"},{value:"default",label:"Cozy"},{value:"airy",label:"Airy"}]} />
              <window.TweakRadio label="Corners" value={tweaks.radius} onChange={v => setTweak("radius", v)}
                options={[{value:"square",label:"Sharp"},{value:"soft",label:"Soft"},{value:"round",label:"Round"}]} />
              <window.TweakRadio label="Type weight" value={tweaks.typeWeight} onChange={v => setTweak("typeWeight", v)}
                options={[{value:"light",label:"Light"},{value:"default",label:"Bold"},{value:"bold",label:"Heavy"}]} />
            </window.TweakSection>
            <window.TweakSection title="Mascots">
              <window.TweakRadio label="Slothy placement" value={tweaks.mascot} onChange={v => setTweak("mascot", v)}
                options={[{value:"hidden",label:"Hidden"},{value:"footer",label:"Footer"},{value:"both",label:"Footer + Hero"}]} />
            </window.TweakSection>
            <window.TweakSection title="Sections">
              <window.TweakToggle label="What is CRMaps" value={tweaks.showWhat} onChange={v => setTweak("showWhat", v)} />
              <window.TweakToggle label="Regional Maps"  value={tweaks.showRegions} onChange={v => setTweak("showRegions", v)} />
              <window.TweakToggle label="Directory"      value={tweaks.showCategories} onChange={v => setTweak("showCategories", v)} />
              <window.TweakToggle label="Mobile App"     value={tweaks.showMobile} onChange={v => setTweak("showMobile", v)} />
              <window.TweakToggle label="Waze & Google"  value={tweaks.showNavigate} onChange={v => setTweak("showNavigate", v)} />
              <window.TweakToggle label="Community"      value={tweaks.showCommunity} onChange={v => setTweak("showCommunity", v)} />
              <window.TweakToggle label="Contact"        value={tweaks.showContact} onChange={v => setTweak("showContact", v)} />
            </window.TweakSection>
          </window.TweaksPanel>
        </React.Fragment>
      );
    }

    ReactDOM.createRoot(document.getElementById("root")).render(<App />);
    })();
  